import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { ShieldOff } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/states';
import { PageContainer } from '@/app/shell/app-shell';
import { useSession } from '@/app/providers/session-provider';

/**
 * Layout route gating every page route on the Access Control grid.
 *
 * One gate, deliberately. The console used to carry role guards here *and* a
 * per-item permission rule on the sidebar, and the two disagreed: a page granted
 * in Access Control stayed unreachable because the coarser gate still said no.
 * The grid is now the single authority, and it folds the old role rules in as
 * each user's default (see backend/services/console_access.py).
 *
 * This is still a UX affordance, not the security boundary — the same rules run
 * server-side on every endpoint. Sitting on the layout rather than on each of
 * the ~40 route elements means a route added later is covered the moment its
 * page appears in the sidebar. Paths that map to no page key (appearance
 * settings, the 404) pass through.
 */
export function PageAccessBoundary() {
  const location = useLocation();
  const { pageKeyForPath, canViewPage } = useSession();
  const pageKey = pageKeyForPath(location.pathname);
  if (pageKey && !canViewPage(pageKey)) return <AccessDenied />;
  return <Outlet />;
}

export function AccessDenied() {
  const location = useLocation();
  return (
    <PageContainer>
      <EmptyState
        icon={<ShieldOff className="size-5" />}
        title="You do not have access to this page"
        description={`Your role does not include ${location.pathname}. Ask an administrator if you need it.`}
        action={
          <Button variant="secondary" size="sm" asChild>
            <a href="/admin/">Back to the dashboard</a>
          </Button>
        }
      />
    </PageContainer>
  );
}

/**
 * Land on the dashboard when it is reachable, otherwise the first page this
 * user actually has. Reading the grid rather than the role means somebody
 * granted only, say, Contacts still lands somewhere they can see.
 */
export function RoleAwareIndex() {
  const { canViewPage, bootstrap } = useSession();
  if (canViewPage('dashboard')) return <Navigate to="/dashboard" replace />;
  if (canViewPage('calls')) return <Navigate to="/calls" replace />;
  const first = bootstrap.navigation.flatMap((section) => section.items).find((item) => item.route);
  return <Navigate to={first?.route ?? '/calls'} replace />;
}
